/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { createContext, Fragment, type ReactNode, useContext, useEffect, useState } from 'react';

import { css, cssMap, jsx } from '@atlaskit/css';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { FieldId } from './field-id-context';

type MessageAppearance = 'default' | 'error' | 'valid';

/**
 * API for the internal `<Message />` component. This is not public API.
 */
interface InternalMessageProps {
	/**
	 * The content of the message
	 */
	children: ReactNode;
	/**
	 * A testId prop is provided for specified elements, which is a unique string
	 *  that appears as a data attribute data-testid in the rendered code,
	 *  serving as a hook for automated tests
	 */
	testId?: string;
	/**
	 * Determines the appearance of the message.
	 */
	appearance?: MessageAppearance;
	fieldId?: string;
}
/**
 * Public API of the various message components.
 */
type MessageProps = Pick<InternalMessageProps, 'children' | 'testId'>;

const messageStyles = css({
	display: 'flex',
	justifyContent: 'baseline',
	gap: token('space.075', '6px'),
	font: token('font.body.small'),
	marginBlockStart: token('space.050', '4px'),
});

const messageAppearanceStyles = cssMap({
	default: {
		color: token('color.text.subtlest', '#6B778C'),
	},
	error: {
		color: token('color.text.danger', '#AE2A19'),
	},
	valid: {
		color: token('color.text.success', '#216E4E'),
	},
});

const iconWrapperStyles = cssMap({
	root: {
		display: 'flex',
		height: '16px',
		alignItems: 'center',
	},
});

const IconWrapper = ({ children }: { children: ReactNode }) => (
	<span css={iconWrapperStyles.root}>{children}</span>
);

const messageIcons: Partial<Record<MessageAppearance, JSX.Element>> = {
	error: <ErrorIcon color="currentColor" label="error" size="small" />,
	valid: <SuccessIcon color="currentColor" label="success" size="small" />,
};

const Message = ({ children, appearance = 'default', fieldId, testId }: InternalMessageProps) => {
	const icon = messageIcons[appearance];
	const { isWrapper } = useContext(MessageWrapperContext);
	const [shouldRenderAfterDelay, setShouldRenderAfterDelay] = useState(false);

	// TODO: DSP-23603 - To clean up when removing "platform_dst_form_screenreader_message_fix" feature flag
	const isDelayRenderEnabled = fg('platform_dst_form_screenreader_message_fix') && !isWrapper;

	useEffect(() => {
		if (isDelayRenderEnabled) {
			// Only delay rendering if there is no wrapper and feature flag is enabled
			const timer = setTimeout(() => {
				setShouldRenderAfterDelay(true);
			}, 10);
			return () => clearTimeout(timer);
		}

		return;
	}, [isDelayRenderEnabled]);

	/**
	 * The wrapping span is necessary to preserve spaces between children.
	 * Otherwise the flex layout of the message will remove any whitespace
	 * between children.
	 *
	 * If the child is just a string, this is not required and we can use one
	 * less DOM element.
	 */
	const content = typeof children === 'string' ? children : <span>{children}</span>;

	const childrenToRender = (
		<Fragment>
			{icon && <IconWrapper>{icon}</IconWrapper>}
			{content}
		</Fragment>
	);
	return (
		<div
			css={[messageStyles, messageAppearanceStyles[appearance]]}
			data-testid={testId}
			id={fieldId}
			// For backwards compatability, if there is a wrapper, aria-live is not needed
			// TODO: DSP-23603 - To clean up when removing "platform_dst_form_screenreader_message_fix" feature flag
			aria-live={!isWrapper ? 'polite' : undefined}
		>
			{/* TODO: DSP-23603 - To clean up when removing "platform_dst_form_screenreader_message_fix" feature flag */}
			{isDelayRenderEnabled ? shouldRenderAfterDelay && childrenToRender : childrenToRender}
		</div>
	);
};

/**
 * __Helper message__
 *
 * A helper message tells the user what kind of input the field takes. For example, a helper message could be
 * 'Password should be more than 4 characters'
 *
 */
export const HelperMessage: ({ children, testId }: MessageProps) => JSX.Element = ({ children, testId }: MessageProps) => (
	<FieldId.Consumer>
		{(fieldId) => (
			<Message fieldId={fieldId ? `${fieldId}-helper` : undefined} testId={testId}>
				{children}
			</Message>
		)}
	</FieldId.Consumer>
);

/**
 * __Error message__
 *
 * An error message is used to tell a user that the field input is invalid. For example, an error message could be
 * 'Invalid username, needs to be more than 4 characters'.
 *
 */
export const ErrorMessage: ({ children, testId }: MessageProps) => JSX.Element = ({ children, testId }: MessageProps) => (
	<FieldId.Consumer>
		{(fieldId) => (
			<Message
				appearance="error"
				fieldId={fieldId ? `${fieldId}-error` : undefined}
				testId={testId}
			>
				{children}
			</Message>
		)}
	</FieldId.Consumer>
);

/**
 * __Valid message__
 *
 * A valid message is used to tell a user that the field input is valid. For example,
 * a helper message could be 'Nice one, this username is available'.
 *
 */
export const ValidMessage: ({ children, testId }: MessageProps) => JSX.Element = ({ children, testId }: MessageProps) => (
	<FieldId.Consumer>
		{(fieldId) => (
			<Message
				appearance="valid"
				fieldId={fieldId ? `${fieldId}-valid` : undefined}
				testId={testId}
			>
				{children}
			</Message>
		)}
	</FieldId.Consumer>
);

/**
 * __Message wrapper context__
 *
 * A message wrapper context allows the children to check
 * if it is contained within the MessageWrapper.
 */
const MessageWrapperContext = createContext<{ isWrapper: boolean }>({
	isWrapper: false,
});

/**
 * __Message wrapper __
 *
 * A message wrapper is used to allow assistive technologies, like screen readers, to announce error or
 * valid messages. This must be loaded into the DOM before the
 * ErrorMessage, ValidMessage is loaded. Otherwise, assistive technologies
 * may not render the message.
 *
 */
export const MessageWrapper: ({ children }: MessageProps) => JSX.Element = ({ children }: MessageProps) => {
	const contextValue = {
		isWrapper: true,
	};

	return (
		<MessageWrapperContext.Provider value={contextValue}>
			<div aria-live="polite" data-testid="message-wrapper">
				{children}
			</div>
		</MessageWrapperContext.Provider>
	);
};
